import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useAuth } from "@/App";
import { CRMLayout } from "@/components/CRMLayout";
import { toast } from "sonner";
import {
  Loader2, RefreshCw, Globe, ExternalLink, Copy, Eye, EyeOff, Lock, LockOpen,
  Server, Boxes, Cog, Plus, Pencil, Trash2, KeyRound, Database, FolderOpen,
  ShieldCheck, ShieldAlert, Search, Link2, Building2, Terminal, FolderKanban,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const CATEGORIES = [
  { id: "public", label: "Site public", classe: "bg-emerald-100 text-emerald-700" },
  { id: "odoo", label: "Odoo", classe: "bg-violet-100 text-violet-700" },
  { id: "interne", label: "Outil interne", classe: "bg-indigo-100 text-indigo-700" },
  { id: "api", label: "API / service", classe: "bg-sky-100 text-sky-700" },
  { id: "test", label: "Test / démo", classe: "bg-amber-100 text-amber-700" },
  { id: "autre", label: "Autre", classe: "bg-slate-100 text-slate-600" },
];
const catInfo = (id) => CATEGORIES.find((c) => c.id === id);

const ICONE_SERVICE = { pm2: Server, docker: Boxes, systemd: Cog, process: Terminal };

const copier = (texte, quoi) => {
  navigator.clipboard.writeText(texte || "");
  toast.success(`${quoi} copié`);
};

/** Vert = le site répond, ambre = il répond une erreur, rouge = il ne répond pas. */
const teinteEtat = (health) => {
  if (!health) return "bg-slate-300";
  if (!health.status) return "bg-red-500";
  if (health.status >= 500) return "bg-red-500";
  // 401/403 vient du mot de passe nginx : le site est debout, il se protege.
  if (health.status === 401 || health.status === 403) return "bg-emerald-500";
  if (health.status >= 400) return "bg-amber-500";
  return "bg-emerald-500";
};

const libelleEtat = (health) => {
  if (!health) return "état inconnu";
  if (!health.status) return `injoignable — ${health.error || "pas de réponse"}`;
  if (health.status === 401 || health.status === 403)
    return `en ligne, protégé par mot de passe · ${health.ms} ms`;
  return `HTTP ${health.status} · ${health.ms} ms`;
};

const Champ = ({ icon: Icon, children, title }) => (
  <span className="inline-flex items-center gap-1.5 text-xs text-slate-600 bg-slate-50
                   border border-slate-200 rounded-md px-2 py-1" title={title}>
    <Icon className="w-3.5 h-3.5 text-slate-400 shrink-0" />
    <span className="truncate max-w-[220px]">{children}</span>
  </span>
);

const Compte = ({ compte }) => {
  const [visible, setVisible] = useState(false);
  return (
    <div className="flex items-center gap-2 text-sm bg-white border border-slate-200 rounded-lg px-3 py-2">
      <KeyRound className="w-4 h-4 text-slate-400 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="font-medium text-slate-800 truncate">{compte.label || "Compte"}</span>
          {compte.url && (
            <a href={compte.url} target="_blank" rel="noreferrer"
               className="text-xs text-indigo-600 hover:underline truncate">{compte.url}</a>
          )}
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-600 mt-0.5">
          <span className="font-mono truncate">{compte.username || "—"}</span>
          {compte.username && (
            <button onClick={() => copier(compte.username, "Identifiant")}
                    className="text-slate-400 hover:text-indigo-600" title="Copier l'identifiant">
              <Copy className="w-3 h-3" />
            </button>
          )}
          <span className="text-slate-300">|</span>
          <span className="font-mono truncate">
            {compte.password ? (visible ? compte.password : "••••••••") : "—"}
          </span>
          {compte.password && (
            <>
              <button onClick={() => setVisible((v) => !v)}
                      className="text-slate-400 hover:text-indigo-600"
                      title={visible ? "Masquer" : "Afficher"}>
                {visible ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
              </button>
              <button onClick={() => copier(compte.password, "Mot de passe")}
                      className="text-slate-400 hover:text-indigo-600" title="Copier le mot de passe">
                <Copy className="w-3 h-3" />
              </button>
            </>
          )}
        </div>
        {compte.note && <p className="text-xs text-slate-500 mt-1">{compte.note}</p>}
      </div>
    </div>
  );
};

const CarteSite = ({ site, onEdit }) => {
  const [comptesOuverts, setComptesOuverts] = useState(false);
  const cat = catInfo(site.category);
  const url = site.urls?.[0];
  const cert = site.cert_days_left;

  return (
    <div className={`bg-white rounded-xl border p-5 flex flex-col gap-3
                     ${site.hidden ? "border-dashed border-slate-200 opacity-60" : "border-slate-200"}`}>
      <div className="flex items-start gap-3">
        <span className={`w-2.5 h-2.5 rounded-full mt-2 shrink-0 ${teinteEtat(site.health)}`}
              title={libelleEtat(site.health)} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-slate-900 truncate">{site.name}</h3>
            {cat && <span className={`text-xs px-2 py-0.5 rounded-full ${cat.classe}`}>{cat.label}</span>}
            {site.societe && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600
                               inline-flex items-center gap-1">
                <Building2 className="w-3 h-3" />{site.societe}
              </span>
            )}
            {site.source === "manuel" && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">saisi</span>
            )}
          </div>
          {site.description && <p className="text-sm text-slate-600 mt-1">{site.description}</p>}
          <p className="text-xs text-slate-400 mt-1">{libelleEtat(site.health)}</p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {url && (
            <Button size="sm" variant="outline" onClick={() => window.open(url, "_blank")}>
              <ExternalLink className="w-3.5 h-3.5 mr-1" /> Ouvrir
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => onEdit(site)} title="Modifier les informations">
            <Pencil className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Adresses */}
      <div className="flex flex-wrap gap-2">
        {(site.urls || []).map((u) => (
          <span key={u} className="inline-flex items-center gap-1.5 text-xs bg-indigo-50
                                   border border-indigo-100 text-indigo-700 rounded-md px-2 py-1">
            <Globe className="w-3.5 h-3.5" />
            <a href={u} target="_blank" rel="noreferrer" className="hover:underline truncate max-w-[260px]">{u}</a>
            <button onClick={() => copier(u, "Lien")} className="text-indigo-400 hover:text-indigo-700">
              <Copy className="w-3 h-3" />
            </button>
          </span>
        ))}
        {(site.links || []).map((l, i) => (
          <a key={i} href={l.url} target="_blank" rel="noreferrer"
             className="inline-flex items-center gap-1.5 text-xs bg-white border border-slate-200
                        text-slate-700 rounded-md px-2 py-1 hover:border-indigo-300">
            <Link2 className="w-3.5 h-3.5 text-slate-400" />
            <span className="truncate max-w-[220px]">{l.label || l.url}</span>
          </a>
        ))}
      </div>

      {/* Faits techniques */}
      <div className="flex flex-wrap gap-2">
        {(site.services || []).map((s, i) => {
          const Icone = ICONE_SERVICE[s.kind] || Server;
          return (
            <span key={i}
                  className={`inline-flex items-center gap-1.5 text-xs rounded-md px-2 py-1 border
                              ${s.status === "online"
                                ? "bg-emerald-50 border-emerald-100 text-emerald-700"
                                : "bg-red-50 border-red-100 text-red-700"}`}
                  title={s.detail || ""}>
              <Icone className="w-3.5 h-3.5" />
              {s.name || s.detail || "inconnu"} <span className="text-slate-400">:{s.port}</span>
            </span>
          );
        })}
        {site.ssl ? (
          <Champ icon={cert !== null && cert < 21 ? ShieldAlert : ShieldCheck}
                 title={site.cert_expires_at || ""}>
            HTTPS{cert !== null && cert !== undefined ? ` · ${cert} j` : ""}
          </Champ>
        ) : (
          site.urls?.length > 0 && <Champ icon={LockOpen} title="Pas de certificat sur ce vhost">HTTP seul</Champ>
        )}
        {site.auth_basic && <Champ icon={Lock} title="Point d'entrée *.dev.novaforge.pro : mot de passe nginx">Mot de passe nginx</Champ>}
        {site.interne && (
          <Champ icon={ShieldCheck}
                 title="Aucun port ouvert sur Internet : joignable seulement depuis le serveur">
            Accès interne (pare-feu)
          </Champ>
        )}
        {site.database && <Champ icon={Database}>{site.database}</Champ>}
        {(site.folder_path || site.roots?.[0]) && (
          <Champ icon={FolderOpen} title={site.folder_path || site.roots?.[0]}>
            {site.folder_path || site.roots?.[0]}
          </Champ>
        )}
        {site.tech && <Champ icon={Cog}>{site.tech}</Champ>}
        {site.conf && <Champ icon={Server} title={site.conf_path}>{site.conf}</Champ>}
        {site.project_name && (
          <Link to={`/dashboard/projects/${site.project_id}`}
                className="inline-flex items-center gap-1.5 text-xs text-indigo-700 bg-indigo-50
                           border border-indigo-100 rounded-md px-2 py-1 hover:border-indigo-300">
            <FolderKanban className="w-3.5 h-3.5" /> Projet : {site.project_name}
          </Link>
        )}
      </div>

      {site.notes && (
        <p className="text-sm text-slate-600 bg-slate-50 border border-slate-100 rounded-lg p-3 whitespace-pre-wrap">
          {site.notes}
        </p>
      )}

      {(site.accounts || []).length > 0 && (
        <div>
          <button onClick={() => setComptesOuverts((v) => !v)}
                  className="text-sm font-medium text-slate-700 inline-flex items-center gap-2 hover:text-indigo-600">
            <Lock className="w-4 h-4" />
            Accès ({site.accounts.length})
            <span className="text-xs text-slate-400">{comptesOuverts ? "masquer" : "afficher"}</span>
          </button>
          {comptesOuverts && (
            <div className="mt-2 space-y-2">
              {site.accounts.map((c, i) => <Compte key={i} compte={c} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ── Formulaire d'édition ───────────────────────────────────────────── */

const VIDE = {
  label: "", category: "", societe: "", description: "", tech: "", database: "",
  folder_path: "", restart_command: "", project_id: "", notes: "",
  links: [], accounts: [], hidden: false, pinned: false, name: "", url: "",
};

const DialogueSite = ({ site, projets, onClose, onSaved }) => {
  const { token } = useAuth();
  const [form, setForm] = useState(VIDE);
  const [saving, setSaving] = useState(false);
  const nouveau = site?.id === undefined;

  useEffect(() => {
    if (!site) return;
    setForm({
      ...VIDE,
      label: site.source === "manuel" ? site.name || "" : (site.has_meta ? site.name : ""),
      category: site.category || "",
      societe: site.societe || "",
      description: site.description || "",
      tech: site.tech || "",
      database: site.database || "",
      folder_path: site.folder_path || "",
      restart_command: site.restart_command || "",
      project_id: site.project_id || "",
      notes: site.notes || "",
      links: site.links || [],
      accounts: site.accounts || [],
      hidden: !!site.hidden,
      pinned: !!site.pinned,
      url: site.source === "manuel" ? site.urls?.[0] || "" : "",
    });
  }, [site]);

  const maj = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const majListe = (cle, i, k, v) =>
    setForm((f) => ({ ...f, [cle]: f[cle].map((el, j) => (j === i ? { ...el, [k]: v } : el)) }));
  const ajouter = (cle, modele) => setForm((f) => ({ ...f, [cle]: [...f[cle], modele] }));
  const retirer = (cle, i) => setForm((f) => ({ ...f, [cle]: f[cle].filter((_, j) => j !== i) }));

  const enregistrer = async () => {
    setSaving(true);
    try {
      const auth = { headers: { Authorization: `Bearer ${token}` } };
      if (nouveau) {
        if (!form.label.trim()) { toast.error("Donnez un nom au site"); setSaving(false); return; }
        await axios.post(`${API_URL}/api/sites`, form, auth);
      } else {
        await axios.put(`${API_URL}/api/sites/${encodeURIComponent(site.id)}`, form, auth);
      }
      toast.success("Site enregistré");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Enregistrement impossible");
    } finally {
      setSaving(false);
    }
  };

  const supprimer = async () => {
    try {
      await axios.delete(`${API_URL}/api/sites/${encodeURIComponent(site.id)}`,
                         { headers: { Authorization: `Bearer ${token}` } });
      toast.success(site.source === "manuel" ? "Site supprimé" : "Annotations effacées");
      onSaved();
    } catch (e) {
      toast.error("Suppression impossible");
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{nouveau ? "Ajouter un site" : `Informations — ${site.name}`}</DialogTitle>
          <DialogDescription>
            {nouveau
              ? "Pour un site hébergé ailleurs ou servi hors nginx."
              : "Ce que le serveur ne peut pas deviner : à quoi sert ce site, pour qui, avec quels accès."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Nom affiché</Label>
              <Input value={form.label} onChange={(e) => maj("label", e.target.value)}
                     placeholder={site?.name || "Mon site"} />
            </div>
            <div>
              <Label>Catégorie</Label>
              <select className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm"
                      value={form.category} onChange={(e) => maj("category", e.target.value)}>
                <option value="">— non classé —</option>
                {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
          </div>

          {nouveau && (
            <div>
              <Label>Adresse</Label>
              <Input value={form.url} onChange={(e) => maj("url", e.target.value)}
                     placeholder="https://exemple.fr" />
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Société</Label>
              <Input value={form.societe} onChange={(e) => maj("societe", e.target.value)}
                     placeholder="IDMAT OI, RL Construction…" />
            </div>
            <div>
              <Label>Projet NovaForge</Label>
              <select className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm"
                      value={form.project_id} onChange={(e) => maj("project_id", e.target.value)}>
                <option value="">— aucun —</option>
                {projets.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          </div>

          <div>
            <Label>À quoi ça sert</Label>
            <Input value={form.description} onChange={(e) => maj("description", e.target.value)}
                   placeholder="Site vitrine, back-office, ERP de la société…" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Technologie</Label>
              <Input value={form.tech} onChange={(e) => maj("tech", e.target.value)}
                     placeholder="FastAPI + React, Odoo 19…" />
            </div>
            <div>
              <Label>Base de données</Label>
              <Input value={form.database} onChange={(e) => maj("database", e.target.value)}
                     placeholder="rlc19, mongo odo_smart…" />
            </div>
            <div>
              <Label>Dossier sur le serveur</Label>
              <Input value={form.folder_path} onChange={(e) => maj("folder_path", e.target.value)}
                     placeholder="/opt/mon-appli" />
            </div>
            <div>
              <Label>Commande de redémarrage</Label>
              <Input value={form.restart_command} onChange={(e) => maj("restart_command", e.target.value)}
                     placeholder="pm2 restart mon-appli" />
            </div>
          </div>

          {/* Accès */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="flex items-center gap-2"><KeyRound className="w-4 h-4" /> Accès</Label>
              <Button size="sm" variant="outline"
                      onClick={() => ajouter("accounts", { label: "", url: "", username: "", password: "", note: "" })}>
                <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter un compte
              </Button>
            </div>
            <div className="space-y-2">
              {form.accounts.map((c, i) => (
                <div key={i} className="border border-slate-200 rounded-lg p-3 space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <Input placeholder="Rôle (admin, client…)" value={c.label}
                           onChange={(e) => majListe("accounts", i, "label", e.target.value)} />
                    <Input placeholder="Adresse de connexion" value={c.url}
                           onChange={(e) => majListe("accounts", i, "url", e.target.value)} />
                    <Input placeholder="Identifiant" value={c.username}
                           onChange={(e) => majListe("accounts", i, "username", e.target.value)} />
                    <Input placeholder="Mot de passe" value={c.password}
                           onChange={(e) => majListe("accounts", i, "password", e.target.value)} />
                  </div>
                  <div className="flex gap-2">
                    <Input placeholder="Note" value={c.note}
                           onChange={(e) => majListe("accounts", i, "note", e.target.value)} />
                    <Button size="sm" variant="ghost" onClick={() => retirer("accounts", i)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Liens utiles */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="flex items-center gap-2"><Link2 className="w-4 h-4" /> Liens utiles</Label>
              <Button size="sm" variant="outline" onClick={() => ajouter("links", { label: "", url: "" })}>
                <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter un lien
              </Button>
            </div>
            <div className="space-y-2">
              {form.links.map((l, i) => (
                <div key={i} className="flex gap-2">
                  <Input placeholder="Back-office, admin…" value={l.label}
                         onChange={(e) => majListe("links", i, "label", e.target.value)} />
                  <Input placeholder="https://…" value={l.url}
                         onChange={(e) => majListe("links", i, "url", e.target.value)} />
                  <Button size="sm" variant="ghost" onClick={() => retirer("links", i)}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label>Notes</Label>
            <Textarea rows={3} value={form.notes} onChange={(e) => maj("notes", e.target.value)}
                      placeholder="Particularités, pièges, à faire…" />
          </div>

          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <Switch checked={form.pinned} onCheckedChange={(v) => maj("pinned", v)} />
              <span className="text-sm text-slate-700">Épingler en tête</span>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.hidden} onCheckedChange={(v) => maj("hidden", v)} />
              <span className="text-sm text-slate-700">Masquer (obsolète)</span>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          {!nouveau && (
            <Button variant="ghost" onClick={supprimer} className="mr-auto text-red-600">
              <Trash2 className="w-4 h-4 mr-1" />
              {site.source === "manuel" ? "Supprimer" : "Effacer les infos"}
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={enregistrer} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ── Page ───────────────────────────────────────────────────────────── */

export default function SitesPage() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [projets, setProjets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [recherche, setRecherche] = useState("");
  const [filtre, setFiltre] = useState("tous");
  const [masques, setMasques] = useState(false);
  const [edition, setEdition] = useState(null);
  const authRef = useRef({ headers: { Authorization: `Bearer ${token}` } });
  authRef.current = { headers: { Authorization: `Bearer ${token}` } };

  const charger = useCallback(async (silencieux = false) => {
    silencieux ? setRefreshing(true) : setLoading(true);
    try {
      const [sites, projs] = await Promise.all([
        axios.get(`${API_URL}/api/sites?check=true`, authRef.current),
        axios.get(`${API_URL}/api/projects`, authRef.current).catch(() => ({ data: [] })),
      ]);
      setData(sites.data);
      setProjets(projs.data || []);
    } catch (e) {
      toast.error("Impossible de charger les sites");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const sites = useMemo(() => {
    const q = recherche.trim().toLowerCase();
    return (data?.sites || [])
      .filter((s) => (masques ? true : !s.hidden))
      .filter((s) => (filtre === "tous" ? true
        : filtre === "sans" ? !s.category : s.category === filtre))
      .filter((s) => !q || JSON.stringify([s.name, s.description, s.domains, s.societe,
        s.tech, s.database, s.notes, s.conf, s.urls]).toLowerCase().includes(q));
  }, [data, recherche, filtre, masques]);

  const t = data?.totals || {};

  return (
    <CRMLayout>
      <div className="p-6 lg:p-8 max-w-[1400px] mx-auto">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Globe className="w-6 h-6 text-indigo-600" /> Sites installés
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              Tout ce qui tourne sur le serveur : adresse, service, certificat, accès.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => charger(true)} disabled={refreshing}>
              {refreshing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                          : <RefreshCw className="w-4 h-4 mr-1" />}
              Actualiser
            </Button>
            <Button onClick={() => setEdition({})}>
              <Plus className="w-4 h-4 mr-1" /> Ajouter un site
            </Button>
          </div>
        </div>

        {/* Compteurs */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          {[
            { label: "Sites", valeur: t.total ?? "—", classe: "text-slate-900" },
            { label: "En ligne", valeur: t.en_ligne ?? "—", classe: "text-emerald-600" },
            { label: "Sans réponse", valeur: t.hors_ligne ?? "—", classe: "text-red-600" },
            { label: "En HTTPS", valeur: t.https ?? "—", classe: "text-slate-900" },
            { label: "Certif < 21 j", valeur: t.certif_bientot ?? "—", classe: "text-amber-600" },
          ].map((c) => (
            <div key={c.label} className="bg-white rounded-xl border border-slate-200 p-4">
              <p className="text-xs text-slate-500">{c.label}</p>
              <p className={`text-2xl font-bold ${c.classe}`}>{c.valeur}</p>
            </div>
          ))}
        </div>

        {/* Filtres */}
        <div className="flex items-center gap-2 flex-wrap mb-5">
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input className="pl-9 w-64" placeholder="Rechercher un site…" value={recherche}
                   onChange={(e) => setRecherche(e.target.value)} />
          </div>
          {[{ id: "tous", label: "Tous" }, ...CATEGORIES, { id: "sans", label: "Non classés" }].map((c) => (
            <button key={c.id} onClick={() => setFiltre(c.id)}
                    className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${
                      filtre === c.id ? "bg-indigo-600 text-white border-indigo-600"
                                      : "bg-white text-slate-600 border-slate-200 hover:border-indigo-300"}`}>
              {c.label}
            </button>
          ))}
          <button onClick={() => setMasques((v) => !v)}
                  className={`text-sm px-3 py-1.5 rounded-full border inline-flex items-center gap-1.5 ${
                    masques ? "bg-slate-800 text-white border-slate-800"
                            : "bg-white text-slate-600 border-slate-200"}`}>
            <EyeOff className="w-3.5 h-3.5" /> Masqués ({t.masques ?? 0})
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        ) : sites.length === 0 ? (
          <p className="text-slate-500 py-12 text-center">Aucun site ne correspond.</p>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {sites.map((s) => <CarteSite key={s.id} site={s} onEdit={setEdition} />)}
          </div>
        )}

        {data?.collected_at && (
          <p className="text-xs text-slate-400 mt-6">
            Relevé du {new Date(data.collected_at).toLocaleString("fr-FR")}
          </p>
        )}
      </div>

      {edition && (
        <DialogueSite site={edition} projets={projets}
                      onClose={() => setEdition(null)}
                      onSaved={() => { setEdition(null); charger(true); }} />
      )}
    </CRMLayout>
  );
}
